import {
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from 'reactstrap';
import { InvoicePaymentDetailDto, PagingDto } from '../../types';
import { PaymentType, PaymentTypes } from '../../common/Data';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { Button } from '../../components';
import { PaymentSearchDto } from '../../types/PaymentSearchDto';
import { invoiceService } from '../../services/InvoiceService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';

// Form data interface
interface CashReceiptsReportFormData {
    paymentTypeId?: number;
    startDate: string;
    endDate: string;
    fiscalYear: string;
}

// Validation schema
const validationSchema = yup.object({
    paymentTypeId: yup.number().optional(),
    startDate: yup.string().optional(),
    endDate: yup.string().when('startDate', (startDate, schema) => {
        return startDate && startDate.length > 0
            ? schema.test('end-after-start', 'End date must be after start date', function (value) {
                  if (!value || !startDate) return true;
                  return moment(value).isSameOrAfter(moment(startDate));
              })
            : schema;
    }),
    fiscalYear: yup
        .string()
        .test('is-valid-year', 'Fiscal year must be a 4-digit year', function (value) {
            if (!value || value.length === 0) return true; // Allow empty
            return /^\d{4}$/.test(value); // Only validate if value exists
        })
        .nullable(),
});

const CashReceiptsReport = () => {
    const [receipts, setReceipts] = useState<InvoicePaymentDetailDto[]>();
    const [paging, setPaging] = useState<PagingDto>({
        pageNumber: 1,
        maxPageSize: null,
        pageSize: 10,
    });
    const [search, setSearch] = useState<PaymentSearchDto>({
        paymentTypeId: null,
        startDate: null,
        endDate: null,
        localTime: null,
        fiscalYear: null,
        paging: paging,
    });

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<CashReceiptsReportFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues: {
            paymentTypeId: undefined,
            startDate: '',
            endDate: '',
            fiscalYear: '',
        },
    });

    const watchedValues = watch();

    // Memoized payment type options
    const paymentTypeOptions = useMemo(
        () =>
            PaymentTypes?.map((pType) => ({
                value: PaymentType[pType],
                label: pType,
            })) || [],
        [],
    );

    useEffect(() => {
        const getData = async () => {
            try {
                const response = await invoiceService.getInvoicePayments({
                    ...search,
                    paging: paging,
                });
                setReceipts(response);
            } catch {
                toast.error('Failed to get receipts');
            }
        };

        getData();
    }, [paging, search]);

    const onSubmit = useCallback(
        (formData: CashReceiptsReportFormData) => {
            setSearch({
                ...search,
                paymentTypeId: formData.paymentTypeId ?? null,
                startDate: formData.startDate ? moment(formData.startDate).utc().format('YYYY-MM-DD') : null,
                endDate: formData.endDate ? moment(formData.endDate).utc().format('YYYY-MM-DD') : null,
                fiscalYear: formData.fiscalYear || null,
            });
        },
        [search],
    );

    const clearSearchForm = useCallback(() => {
        setSearch({
            paymentTypeId: null,
            startDate: null,
            endDate: null,
            localTime: null,
            fiscalYear: null,
            paging: paging,
        });
        reset({
            paymentTypeId: undefined,
            startDate: '',
            endDate: '',
            fiscalYear: '',
        });
    }, [reset, paging]);

    const exportReport = useCallback(() => {
        const toastId = toast.info('Generating cash receipts report, this may take a minute...', {
            autoClose: false,
        });

        invoiceService
            .getCashReportExport({
                ...search,
            })
            .then((response) => {
                const downloadLink = window.document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(
                    new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                );
                downloadLink.download = 'Cash Receipts Report.xlsx';
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                toast.update(toastId, {
                    render: 'Cash receipts report generated successfully.',
                    type: 'success',
                    autoClose: 2500,
                    closeOnClick: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Failed to export report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, [search]);

    return (
        <>
            <Card>
                <CardBody>
                    <div className="d-flex align-items-center mb-4" style={{ gap: '1rem' }}>
                        <h3 className="mb-0 mt-1">Paid Invoices</h3>
                        <div className="flex-grow-1"></div>
                        <Button
                            type="button"
                            color="info"
                            onClick={exportReport}
                        >
                            Export Report
                        </Button>
                    </div>
                    <Row>
                        <Col>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="paymentTypeId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Payment Type
                                    </Label>
                                    <Controller
                                        name="paymentTypeId"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="select"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) =>
                                                    field.onChange(e.target.value ? Number(e.target.value) : null)
                                                }
                                                style={{ width: '180px' }}
                                            >
                                                <option value="">Select...</option>
                                                {paymentTypeOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    />
                                    {errors.paymentTypeId && (
                                        <div className="text-danger small mt-1">{errors.paymentTypeId.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="startDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Dates between
                                    </Label>
                                    <Controller
                                        name="startDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                style={{ width: '150px' }}
                                            />
                                        )}
                                    />
                                    {errors.startDate && (
                                        <div className="text-danger small mt-1">{errors.startDate.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="endDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        and
                                    </Label>
                                    <Controller
                                        name="endDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                style={{ width: '150px' }}
                                            />
                                        )}
                                    />
                                    {errors.endDate && (
                                        <div className="text-danger small mt-1">{errors.endDate.message}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="fiscalYear" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Invoice Year
                                    </Label>
                                    <Controller
                                        name="fiscalYear"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                placeholder="Invoice Year"
                                                style={{ width: '300px' }}
                                            />
                                        )}
                                    />
                                    {errors.fiscalYear && (
                                        <div className="text-danger small mt-1">{errors.fiscalYear.message}</div>
                                    )}
                                </div>
                                </div>

                                {/* Button row within the form */}
                                <Row className="mt-3">
                                    <Col className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                            <Button
                                                type="submit"
                                                color="primary"
                                            >
                                                Search
                                            </Button>
                                            <Button
                                                type="button"
                                                color="link"
                                                onClick={clearSearchForm}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                    <div className="mt-3">
                        <h4>Payment Results</h4>
                    </div>
                </CardBody>
                <Table>
                    <thead>
                        <tr>
                            <th>Institution</th>
                            <th>Transaction/Reference #</th>
                            <th>Payment Date</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {receipts ? (
                            receipts
                                .filter((x) => x.isDeleted === false)
                                .map((receipt, i) => (
                                    <tr key={receipt.id ?? i}>
                                        <td>{receipt.invoice.institutionTitle}</td>
                                        <td>
                                            {receipt.paymentType === PaymentType['Credit Card']
                                                ? receipt.transactionId
                                                : receipt.referenceNumber}
                                        </td>
                                        <td>{receipt.paymentDate && new Date(receipt.paymentDate).toDateString()}</td>
                                        <td>{PaymentType[receipt.paymentType]}</td>
                                        <td>${receipt.paymentAmount}</td>
                                        <td>{!receipt.isConfirmed ? 'Processing' : ''}</td>
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan={6}>No Results</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                <CardBody>
                    <Pagination size="sm" aria-label="Invoice Navigation">
                        <PaginationItem disabled={paging.pageNumber === 1}>
                            <PaginationLink
                                previous
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber - 1 })}
                            />
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationLink
                                next
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber + 1 })}
                            />
                        </PaginationItem>
                    </Pagination>
                </CardBody>
            </Card>
        </>
    );
};

export default CashReceiptsReport;
